/**
 * The in-app video library — every "watch a video" in the student app.
 *
 * A video reference is one of three things, and each used to open a new tab:
 *
 *   - ONE video a teacher chose        → played here, in an embedded player;
 *   - a YouTube SEARCH (the sub-topic  → run through `GET /videos/search`, and
 *     fallback, or the search aimed      the three to five videos it finds are
 *     at the student's mistake)          listed and played here;
 *   - any other link                   → the one case that still opens outside,
 *                                        because nothing else can be framed.
 *
 * The player is YouTube's privacy-enhanced embed, and the dialog is portalled
 * to <body>: a task card is a dragged, transformed element, and a fixed overlay
 * inside one is positioned against the card instead of the screen.
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useQuery } from '@tanstack/react-query';
import { youTubeSearchQuery, youTubeVideoId } from '@cwy/config/contracts';
import type { VideoDTO, VideoSearchResultDTO } from '@cwy/config/contracts';
import { apiGet } from '@/lib/api';

export interface VideoSource {
  /** A stored reference: one video's link, or a YouTube search link. */
  videoRef?: string | null;
  /** A search to run instead of (or as well as) the reference's own. */
  query?: string | null;
}

/** What a source resolves to: a video to pin first, and a search for the rest. */
function resolve({ videoRef, query }: VideoSource) {
  const pinnedId = youTubeVideoId(videoRef);
  const search = query?.trim() || youTubeSearchQuery(videoRef);
  // Anything else that is a web link cannot play here; it is the last resort.
  const external =
    !pinnedId && !search && videoRef && /^https?:\/\//.test(videoRef) ? videoRef : null;
  return { pinnedId, search: search || null, external };
}

/** True when a source has anything to show at all. */
export function hasVideo(source: VideoSource): boolean {
  const { pinnedId, search, external } = resolve(source);
  return Boolean(pinnedId || search || external);
}

/**
 * The button that opens the library. Renders nothing for a source with no
 * video, so a caller need not check first.
 */
export function WatchVideosButton({
  source,
  title,
  className,
  children,
  onOpenChange,
}: {
  source: VideoSource;
  /** The dialog's heading — what the videos are about. */
  title: string;
  className?: string;
  children: ReactNode;
  /** For effort timing: told when the player opens and closes. */
  onOpenChange?: (open: boolean) => void;
}) {
  const [open, setOpen] = useState(false);
  const resolved = resolve(source);
  if (!resolved.pinnedId && !resolved.search && !resolved.external) return null;

  // A non-YouTube link is the one thing that cannot be played in place.
  if (resolved.external) {
    return (
      <a href={resolved.external} target="_blank" rel="noreferrer" className={className}>
        {children}
      </a>
    );
  }

  const setOpenAndTell = (next: boolean) => {
    setOpen(next);
    onOpenChange?.(next);
  };

  return (
    <>
      <button type="button" className={className} onClick={() => setOpenAndTell(true)}>
        {children}
      </button>
      {open && (
        <VideoLibraryDialog
          title={title}
          pinnedId={resolved.pinnedId}
          search={resolved.search}
          onClose={() => setOpenAndTell(false)}
        />
      )}
    </>
  );
}

function VideoLibraryDialog({
  title,
  pinnedId,
  search,
  onClose,
}: {
  title: string;
  pinnedId: string | null;
  search: string | null;
  onClose: () => void;
}) {
  const closeRef = useRef<HTMLButtonElement>(null);
  const results = useQuery({
    queryKey: ['videos', search],
    enabled: Boolean(search),
    // A search's answer is cached server-side for a week; no need to ask twice
    // in one sitting.
    staleTime: 60 * 60 * 1000,
    retry: false,
    queryFn: () =>
      apiGet<VideoSearchResultDTO>(`/videos/search?q=${encodeURIComponent(search ?? '')}`),
  });

  const videos = useMemo<VideoDTO[]>(() => {
    const found = results.data?.videos ?? [];
    const pinned: VideoDTO[] = pinnedId
      ? [{ id: pinnedId, title: 'Your teacher’s video', channel: '', thumbnailUrl: null }]
      : [];
    return [...pinned, ...found.filter((video) => video.id !== pinnedId)];
  }, [results.data, pinnedId]);

  const [chosenId, setChosenId] = useState<string | null>(null);
  const playing = videos.find((video) => video.id === chosenId) ?? videos[0] ?? null;

  // Escape closes, and focus starts on the close button — once, on open. The
  // latest `onClose` is read through a ref: the exam re-renders its parent
  // every second, and re-running this would pull focus back each time. Local
  // UI only — no write happens in this effect.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  useEffect(() => {
    closeRef.current?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onCloseRef.current();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const loading = Boolean(search) && results.isPending && videos.length === 0;
  const unavailable = !loading && videos.length === 0;

  return createPortal(
    <div
      className="fixed inset-0 z-[70] flex items-end justify-center bg-black/50 sm:items-center sm:p-4"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="flex max-h-[92vh] w-full max-w-3xl flex-col overflow-hidden rounded-t-2xl bg-card shadow-pop sm:rounded-2xl"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 border-b border-line px-4 py-3">
          <div className="min-w-0">
            <p className="text-fs-11 font-bold uppercase tracking-[0.08em] text-ink-soft">Videos</p>
            <h3 className="truncate text-base font-bold text-forest">{title}</h3>
          </div>
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            aria-label="Close videos"
            className="rounded-lg px-2.5 py-1 text-lg leading-none text-ink-soft transition-colors duration-150 hover:bg-paper-sunk hover:text-ink"
          >
            ×
          </button>
        </div>

        <div className="flex min-h-0 flex-col gap-3 overflow-y-auto p-4">
          {playing && (
            <div className="aspect-video w-full overflow-hidden rounded-xl bg-black">
              <iframe
                key={playing.id}
                src={`https://www.youtube-nocookie.com/embed/${playing.id}?rel=0&modestbranding=1`}
                title={playing.title}
                className="h-full w-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          )}

          {loading && (
            <div className="flex flex-col items-center gap-2 py-10 text-sm text-ink-soft">
              <div className="h-6 w-6 animate-spin rounded-full border-2 border-slate-300 border-t-forest" />
              Finding videos…
            </div>
          )}

          {unavailable && (
            <p className="py-8 text-center text-sm text-ink-soft">
              {results.data && !results.data.configured
                ? 'Videos aren’t available right now.'
                : 'No videos found for this yet.'}{' '}
              Try the theory note, or ask your teacher.
            </p>
          )}

          {videos.length > 1 && (
            <ul className="grid gap-2 sm:grid-cols-2">
              {videos.map((video) => {
                const active = video.id === playing?.id;
                return (
                  <li key={video.id}>
                    <button
                      type="button"
                      onClick={() => setChosenId(video.id)}
                      aria-current={active ? 'true' : undefined}
                      className={`flex w-full items-start gap-2.5 rounded-xl border p-2 text-left transition-colors duration-150 ${
                        active ? 'border-forest bg-forest/5' : 'border-line hover:bg-paper-sunk'
                      }`}
                    >
                      {video.thumbnailUrl ? (
                        <img
                          src={video.thumbnailUrl}
                          alt=""
                          loading="lazy"
                          className="h-[54px] w-24 flex-none rounded-md object-cover"
                        />
                      ) : (
                        <span className="flex h-[54px] w-24 flex-none items-center justify-center rounded-md bg-paper-sunk text-ink-soft">
                          ▶
                        </span>
                      )}
                      <span className="min-w-0">
                        <span className="line-clamp-2 text-sm font-semibold text-ink">
                          {video.title}
                        </span>
                        {video.channel && (
                          <span className="mt-0.5 block truncate text-xs text-ink-soft">
                            {video.channel}
                          </span>
                        )}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
